// components/Customers.jsx
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { 
  Plus, Trash2, ArrowLeft, Edit2, Save, X, 
  User, MapPin, Phone, Hash, Search, RefreshCw 
} from 'lucide-react';
import { useCustomers } from '../hooks/useCustomers';

const Customers = () => {
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    customer_name: '',
    customer_address: '',
    contact_number: '',
    consumer_number: ''
  });
  
  const navigate = useNavigate();
  const { customers, loading, addCustomer, updateCustomer, deleteCustomer, fetchCustomers } = useCustomers();

  // Validation functions
  const validateName = (value) => {
    // Only letters, spaces, dots, and hyphens allowed
    return /^[A-Za-z\s\.\-]*$/.test(value);
  };

  const validateContact = (value) => {
    // Only digits allowed
    return /^\d*$/.test(value);
  };

  const validateConsumerNumber = (value) => {
    // Only digits and optional hyphens allowed (for formats like 123-456-789)
    return /^[\d\-]*$/.test(value);
  };

  const handleInputChange = (field, value) => {
    let isValid = true;
    let processedValue = value;

    switch(field) {
      case 'customer_name':
        // Remove any digits and special characters except spaces, dots, hyphens
        processedValue = value.replace(/[^A-Za-z\s\.\-]/g, '');
        break;
      case 'contact_number':
        // Only allow digits
        processedValue = value.replace(/\D/g, '');
        // Limit to 15 digits
        if (processedValue.length > 15) processedValue = processedValue.slice(0, 15);
        break;
      case 'consumer_number':
        // Only allow digits and hyphens
        processedValue = value.replace(/[^0-9\-]/g, '');
        break;
      case 'customer_address':
        // Allow letters, numbers, spaces, and common address characters
        processedValue = value.replace(/[^A-Za-z0-9\s\.,#\-]/g, '');
        break;
      default:
        processedValue = value;
    }

    setFormData({...formData, [field]: processedValue});
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.customer_name.trim()) {
      toast.error('Customer name is required');
      return;
    }
    if (!formData.consumer_number.trim()) {
      toast.error('Consumer number is required');
      return;
    }
    if (!formData.customer_address.trim()) {
      toast.error('Address is required');
      return;
    }
    if (!formData.contact_number.trim()) {
      toast.error('Contact number is required');
      return;
    }

    // Additional validations
    if (formData.contact_number.length < 10) {
      toast.error('Contact number must be at least 10 digits');
      return;
    }

    if (formData.consumer_number.replace(/-/g, '').length < 3) {
      toast.error('Consumer number must be at least 3 digits');
      return;
    }

    try {
      if (editingId) {
        await updateCustomer(editingId, formData);
      } else {
        await addCustomer(formData);
      }
      
      setShowForm(false);
      setEditingId(null);
      setFormData({ 
        customer_name: '', 
        customer_address: '', 
        contact_number: '', 
        consumer_number: '' 
      });
    } catch (error) {
      // Error already handled in hook
    }
  };

  const handleEdit = (customer) => {
    setEditingId(customer.id);
    setFormData({
      customer_name: customer.customer_name,
      customer_address: customer.customer_address,
      contact_number: customer.contact_number,
      consumer_number: customer.consumer_number
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this customer?')) {
      await deleteCustomer(id);
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingId(null);
    setFormData({ 
      customer_name: '', 
      customer_address: '', 
      contact_number: '', 
      consumer_number: '' 
    });
  };

  // Filter customers based on search
  const filteredCustomers = customers.filter(customer =>
    customer.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    customer.consumer_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    customer.contact_number?.includes(searchTerm)
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading customers...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navbar */}
      <nav className="bg-gradient-to-r from-blue-700 to-blue-800 text-white p-4 shadow-lg sticky top-0 z-50">
        <div className="container mx-auto flex justify-between items-center flex-wrap gap-2">
          <div className="flex items-center gap-4">
            <button 
              onClick={() => navigate('/dashboard')} 
              className="text-white hover:text-gray-200 transition flex items-center"
            >
              <ArrowLeft className="inline-block mr-2" size={20} />
              Back
            </button>
            <h1 className="text-2xl font-bold">👥 Manage Customers</h1>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={fetchCustomers}
              className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded-md transition flex items-center"
              title="Refresh"
            >
              <RefreshCw size={18} />
            </button>
            <button
              onClick={() => {
                setShowForm(!showForm);
                setEditingId(null);
                setFormData({ 
                  customer_name: '', 
                  customer_address: '', 
                  contact_number: '', 
                  consumer_number: '' 
                });
              }}
              className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md transition flex items-center"
            >
              <Plus className="mr-2" size={18} />
              Add Customer
            </button>
          </div>
        </div>
      </nav>

      <div className="container mx-auto p-4 md:p-6">
        {/* Search Bar */}
        <div className="mb-4 flex flex-wrap gap-4">
          <div className="flex-1 min-w-[200px] relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
            <input
              type="text"
              placeholder="Search by name, consumer no, or phone..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-800 bg-white"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="text-sm text-gray-500 flex items-center">
            Total: <span className="font-bold ml-1">{filteredCustomers.length}</span> customers
          </div>
        </div>

        {/* Add/Edit Customer Form */}
        {showForm && (
          <div className="bg-white p-6 rounded-lg shadow-md mb-6 border-l-4 border-blue-500">
            <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
              {editingId ? (
                <>
                  <Edit2 className="mr-2 text-blue-600" size={20} />
                  Edit Customer
                </>
              ) : (
                <>
                  <Plus className="mr-2 text-blue-600" size={20} />
                  Add New Customer
                </>
              )}
            </h2>
            <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Customer Name - Only Letters & Spaces */}
              <div>
                <label className="block text-gray-700 text-sm font-semibold mb-1 flex items-center gap-1">
                  <User size={16} className="text-blue-600" />
                  Customer Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  placeholder="Enter full name (e.g., Rajesh Kumar)"
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-800"
                  value={formData.customer_name}
                  onChange={(e) => handleInputChange('customer_name', e.target.value)}
                  required
                />
                <p className="text-xs text-gray-400 mt-1">Only letters, spaces, dots, and hyphens allowed</p>
              </div>

              {/* Consumer Number - Only Digits & Hyphens */}
              <div>
                <label className="block text-gray-700 text-sm font-semibold mb-1 flex items-center gap-1">
                  <Hash size={16} className="text-blue-600" />
                  Consumer Number <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  placeholder="Enter consumer number (e.g., 123456 or 123-456-789)"
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-800"
                  value={formData.consumer_number}
                  onChange={(e) => handleInputChange('consumer_number', e.target.value)}
                  required
                />
                <p className="text-xs text-gray-400 mt-1">Only digits and hyphens allowed</p>
              </div>

              {/* Address */}
              <div>
                <label className="block text-gray-700 text-sm font-semibold mb-1 flex items-center gap-1">
                  <MapPin size={16} className="text-blue-600" />
                  Address <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  placeholder="Enter full address (e.g., 123, Main Street, City)"
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-800"
                  value={formData.customer_address}
                  onChange={(e) => handleInputChange('customer_address', e.target.value)}
                  required
                />
                <p className="text-xs text-gray-400 mt-1">Letters, numbers, spaces, and common punctuation allowed</p>
              </div>

              {/* Contact Number - Only Digits */}
              <div>
                <label className="block text-gray-700 text-sm font-semibold mb-1 flex items-center gap-1">
                  <Phone size={16} className="text-blue-600" />
                  Contact Number <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  placeholder="Enter 10-digit mobile number (e.g., 9876543210)"
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-800"
                  value={formData.contact_number}
                  onChange={(e) => handleInputChange('contact_number', e.target.value)}
                  required
                  minLength="10"
                  maxLength="15"
                />
                <p className="text-xs text-gray-400 mt-1">Only digits allowed (10-15 digits)</p>
              </div>

              <div className="md:col-span-2 flex gap-4">
                <button 
                  type="submit" 
                  className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-md transition font-semibold flex items-center"
                >
                  <Save className="mr-2" size={18} />
                  {editingId ? 'Update Customer' : 'Save Customer'}
                </button>
                <button 
                  type="button" 
                  onClick={handleCancel} 
                  className="bg-gray-600 hover:bg-gray-700 text-white px-6 py-2 rounded-md transition font-semibold flex items-center"
                >
                  <X className="mr-2" size={18} />
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Customer Table */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gradient-to-r from-blue-700 to-blue-800 text-white">
                <tr>
                  <th className="px-4 py-3 text-left text-sm">#</th>
                  <th className="px-4 py-3 text-left text-sm">👤 Name</th>
                  <th className="px-4 py-3 text-left text-sm">📍 Address</th>
                  <th className="px-4 py-3 text-left text-sm">📞 Contact</th>
                  <th className="px-4 py-3 text-left text-sm">🔢 Consumer No.</th>
                  <th className="px-4 py-3 text-left text-sm">⚡ Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredCustomers.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="px-6 py-8 text-center text-gray-500">
                      {searchTerm ? 'No customers found matching your search' : 'No customers found. Click "Add Customer" to add your first customer!'}
                    </td>
                  </tr>
                ) : (
                  filteredCustomers.map((customer, index) => (
                    <tr key={customer.id} className="border-b hover:bg-gray-50 transition">
                      <td className="px-4 py-3 text-gray-500 text-sm">{index + 1}</td>
                      <td className="px-4 py-3 text-gray-800 font-medium">{customer.customer_name}</td>
                      <td className="px-4 py-3 text-gray-600 text-sm">{customer.customer_address}</td>
                      <td className="px-4 py-3 text-gray-600">{customer.contact_number}</td>
                      <td className="px-4 py-3">
                        <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-semibold">
                          {customer.consumer_number}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleEdit(customer)}
                            className="text-blue-600 hover:text-blue-800 transition p-1 hover:bg-blue-50 rounded"
                            title="Edit Customer"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(customer.id)}
                            className="text-red-600 hover:text-red-800 transition p-1 hover:bg-red-50 rounded"
                            title="Delete Customer"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Customers;